import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { filter, map, switchMap, takeWhile } from 'rxjs/operators';
import { ActivatedRouteSnapshot, ActivationEnd, Router } from '@angular/router';
import { RouterExtService } from './services/router-ext.service';
import { interval, Observable, Subscription } from 'rxjs';
import { Theme, ThemeService } from './services/theme.service';
import { LoggingService } from './services/logging/logging.service';
import { AnalyticsService } from './services/analytics/analytics.service';
import { ConfigService } from './services/config/config.service';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from './services/config/types/app-config.type';
import { AuthService } from './modules/auth/services/auth.service';
import { CompaniesService } from './modules/companies/services/companies.service';
import { CompanyDetailsInterface } from './modules/companies/interfaces/company-details.interface';
import { SecurityaddonsService } from './modules/securityaddons/services/securityaddons.service';
import { FeatureFlagsService } from './services/feature-flags.service';
import { ConfirmDialogService } from '../common-modules/services/confirm-dialog.service';
import { TermsAndConditionsService } from './modules/auth/services/terms-and-conditions.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
    public languages: string[] = ['en'];
    /* remove this field when the Dark Theme will be required in the application */
    private DisableDarkTheme = true;

    private readonly userSubscription: Subscription;
    private readonly authenticatedSubscription: Subscription;
    private jwtUserSubscription: Subscription;

    /* observable for getting ActivationEnd snapshots on leaf nodes */
    private routerLeafActivationEnd$: Observable<ActivatedRouteSnapshot> = this.router.events.pipe(
        /* get only activation end events */
        filter((event) => {
            return event instanceof ActivationEnd;
        }),
        map((event) => event as ActivationEnd),
        /* get snapshot */
        map((event: ActivationEnd) => {
            return event.snapshot;
        }),
        /* only get leaf route nodes */
        filter((route: ActivatedRouteSnapshot) => {
            return route.firstChild === null;
        }),
    );
    private configSubscription: Subscription;
    private isAlive = true;

    public constructor(
        private router: Router,
        private routerExt: RouterExtService,
        private titleService: Title,
        private themeService: ThemeService,
        private loggingService: LoggingService,
        private analyticsService: AnalyticsService,
        private configService: ConfigService,
        private authService: AuthService,
        private companiesService: CompaniesService,
        private securityAddOnsService: SecurityaddonsService,
        private featureFlagService: FeatureFlagsService,
        private confirmDialogService: ConfirmDialogService,
        private termsAndConditionsService: TermsAndConditionsService,
        private http: HttpClient,
    ) {}

    public async ngOnInit() {
        this.initConfigPolling();

        this.loggingService.info('Application has started!');

        /* init router ext */
        this.routerExt.init();

        /* init analytics */
        this.analyticsService.init(this.configService.AnalyticsConfig);

        this.initPageTitleUpdate();

        this.themeService.changeTheme(Theme.Light);

        this.checkIfUserAcceptedTermsAndConditions();

        this.redirectToRequestedUrl();
    }

    private initConfigPolling(): void {
        this.configSubscription = interval(this.configService.refreshInterval)
            .pipe(
                takeWhile(() => this.isAlive),
                switchMap(() => this.http.get<AppConfig>('/assets/appConfig.json')),
            )
            .subscribe({
                next: (config) => {
                    if (this.configService.isMaintenance !== config.isMaintenance) {
                        this.configService.set(config);
                        window.location.reload();
                    }
                },
                error: (err) => console.error('Error fetching config:', err),
            });
    }

    public ngOnDestroy(): void {
        if (this.userSubscription) {
            this.userSubscription.unsubscribe();
        }

        if (this.authenticatedSubscription) {
            this.authenticatedSubscription.unsubscribe();
        }

        if (this.jwtUserSubscription) {
            this.jwtUserSubscription.unsubscribe();
        }
        if (this.configSubscription) {
            this.configSubscription.unsubscribe();
        }
    }

    @HostListener('document:keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        if (this.DisableDarkTheme) {
            return;
        }

        if (event.shiftKey && event.key === 'Q') {
            this.themeService.changeTheme(Theme.Light);
        } else if (event.shiftKey && event.key === 'W') {
            this.themeService.changeTheme(Theme.Dark);
        }
    }

    private initPageTitleUpdate(): void {
        this.routerLeafActivationEnd$.subscribe((route) => {
            if (route.data.title) {
                this.titleService.setTitle(route.data.title + ' - MDR');
            }
        });
    }

    private checkIfUserAcceptedTermsAndConditions() {
        const jwtUser = this.authService.jwtUser;

        if (jwtUser === null) {
            return;
        }

        const companySubscription = this.companiesService
            .getCompanyDetails(jwtUser.CompanyId)
            .subscribe((company: CompanyDetailsInterface) => {
                companySubscription.unsubscribe();

                if (jwtUser?.meta.termsAndConditionsAccepted || localStorage.getItem('acceptedTC') === 'true') {
                    console.log('User has accepted terms and conditions');
                    return;
                }

                this.showTCDialog(this.featureFlagService.get('terms_and_conditions_url'), jwtUser);
            });
    }

    private showTCDialog(url: string, jwtUser: any) {
        this.confirmDialogService.showDialog(
            ConfirmDialogService.getDialogDataWithSecondaryButton(
                this.termsAndConditionsService.SubmitConfirmTitle,
                this.termsAndConditionsService.SubmitConfirmMessage(url),
                () => {
                    this.termsAndConditionsService.onCreateSuccess(jwtUser.UserId, true);
                },
                this.termsAndConditionsService.SubmitConfirmOptionalMessage,
                $localize`Accept`,
                $localize`Decline`,
                this.termsAndConditionsService.SubmitSecondConfirmMessage(''),
            ),
            {},
            true,
            true,
        );
    }

    private redirectToRequestedUrl(): void {
        if (window.location.href.includes('sso-error')) {
            return;
        }

        const initialUrl = localStorage.getItem('initialUrl');
        const redirectToInitialUrl = localStorage.getItem('redirectToInitialUrl');

        if (initialUrl === null || redirectToInitialUrl !== 'true') {
            return;
        }

        const noLanguagePath = this.getProperPathFromOriginRoutes(initialUrl);

        localStorage.removeItem('initialUrl');
        localStorage.removeItem('redirectToInitialUrl');

        this.router.navigateByUrl(noLanguagePath).catch(console.error);
    }

    private getProperPathFromOriginRoutes(path: string) {
        this.languages.forEach((language) => {
            if (path.startsWith('/' + language)) {
                path = path.substring(path.indexOf(language) + language.length);
            }
        });

        return path;
    }
}
